import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

// Lazy Image Component
const LazyImage = ({ src, alt, className, onError }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={className}>
      {isInView && (
        <img 
          src={src}
          alt={alt}
          className={`w-full h-full object-cover rounded-md transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setIsLoaded(true)}
          onError={onError}
        />
      )}
      {!isLoaded && (
        <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
          <div className="animate-pulse w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded"></div>
        </div>
      )}
    </div>
  );
};

const BackgroundImageSelector = ({ selectedBackground, onBackgroundSelect, error }) => {
  const { language } = useLanguage();
  const [availableBackgrounds, setAvailableBackgrounds] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Background image'larını yükle
  useEffect(() => {
    const backgrounds = [
      { id: 'default-bg', name: 'Varsayılan', filename: 'pexels-pixabay-268533.jpg' },
      { id: 'abstract-blue', name: 'Mavi Soyut', filename: 'pexels-pixabay-33545.jpg' },
      { id: 'abstract-green', name: 'Yeşil Soyut', filename: 'pexels-eberhardgross-1612353.jpg' },
      { id: 'gradient-sunset', name: 'Gün Batımı', filename: 'pexels-daniel-maforte-3709229-5544157.jpg' },
      { id: 'gradient-forest', name: 'Orman Geçişi', filename: 'pexels-rpnickson-2775196.jpg' },
      { id: 'tech-circuit', name: 'Teknoloji Devresi', filename: 'pexels-pok-rie-33563-6029883.jpg' },
      { id: 'minimal-white', name: 'Minimal Beyaz', filename: 'pexels-oscar-garcia-medina-241846767-12302975.jpg' },
      { id: 'nature-beach', name: 'Sahil', filename: 'pexels-nicole-avagliano-1132392-2236703.jpg' },
      { id: 'hotel-modern', name: 'Modern Otel', filename: 'pexels-luis-rodrigues-198790-1687575.jpg' },
      { id: 'geometric-blue', name: 'Geometrik Mavi', filename: 'pexels-mian-rizwan-250562-1681236.jpg' },
      { id: 'Sword', name: 'Sword', filename: 'sword.png' },
      { id: 'ayrikvadi', name: 'Ayrık Vadi', filename: 'ayrikvadi.jpg' }
    ];
    setAvailableBackgrounds(backgrounds);
  }, []);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleBackgroundSelect = (backgroundId) => {
    onBackgroundSelect(backgroundId);
    setIsDropdownOpen(false);
  };

  const selectedBackgroundData = availableBackgrounds.find(bg => bg.id === selectedBackground);

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {language === 'tr' ? 'Proje Arkaplan Resmi' : 'Project Background Image'}
        <span className="text-red-500 ml-1">*</span>
      </label>
      
      {/* Selected Background Display */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className={`
            w-full flex items-center space-x-3 p-3 border rounded-lg transition-all duration-200
            ${isDropdownOpen 
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }
            ${error ? 'border-red-500' : ''}
          `}
        >
          <div className="w-16 h-10 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 overflow-hidden">
            {selectedBackgroundData ? (
              <LazyImage 
                src={`/project-backgrounds/${selectedBackgroundData.filename}`}
                alt={selectedBackgroundData.name}
                className="w-full h-full"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : (
              <span className="text-gray-400 text-lg">🖼️</span>
            )}
            <div className="w-full h-full bg-gray-200 dark:bg-gray-700 items-center justify-center text-xs text-gray-400 hidden">
              ?
            </div>
          </div>
          
          <div className="flex-1 text-left">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {selectedBackgroundData ? selectedBackgroundData.name : (language === 'tr' ? 'Arkaplan seçin...' : 'Select background...')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {language === 'tr' ? 'Tıklayarak diğer arkaplanları görün' : 'Click to see other backgrounds'}
            </p>
          </div>
          
          <div className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        
        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-[60] max-h-80 overflow-y-auto">
            <div className="grid grid-cols-4 gap-2 p-3">
              {availableBackgrounds.map((background) => (
                <button
                  key={background.id}
                  type="button"
                  onClick={() => handleBackgroundSelect(background.id)}
                  className={`
                    relative w-full h-16 flex items-center justify-center rounded-lg border-2 transition-all duration-200 overflow-hidden group
                    ${selectedBackground === background.id
                      ? 'border-blue-500 shadow-lg scale-105'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:scale-102'
                    }
                  `}
                  title={background.name}
                >
                  <LazyImage 
                    src={`/project-backgrounds/${background.filename}`}
                    alt={background.name}
                    className="w-full h-full"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 items-center justify-center text-xs text-gray-400 hidden">
                    <span>Resim bulunamadı</span>
                  </div>
                  
                  {/* Seçili indicator */}
                  {selectedBackground === background.id && (
                    <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200"></div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default BackgroundImageSelector;
